/**
 * PRD.218 — Payment history + totals strip for a single invoice.
 *
 * Renders one row per `invoice_payments` entry plus a header summarising
 * total / paid / balance. All currency math goes through Dinero so the
 * displayed paid/remaining numbers can't drift from what the backend
 * sees. Used in the patient InvoicesTab detail panel and (optionally)
 * the Global Invoice drawer.
 */

import { useMemo } from 'react';
import { Button } from '@helios/ui/button';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qInvoicePaymentsByInvoice, type InvoicePaymentRow } from '@helios/data-access';
import { phpFromCents, phpFromMajor, subtractMoney, sumMoney } from '@helios/domain';
import { useFormatMoney } from '../../hooks/useFormatMoney';

// ─── Method labels ───

const METHOD_LABEL: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  gcash: 'GCash',
  other: 'Other',
};

function methodLabel(m: string | null): string {
  if (!m) return '—';
  return METHOD_LABEL[m] ?? m;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Props ───

interface Props {
  invoiceId: string;
  /** Decimal-major invoice grand total (invoices.grand_total). */
  invoiceGrandTotal: number | null;
  /** Click handler for the "Record Payment" button. */
  onRecordPayment?: () => void;
  /** Hide the action button (e.g. when the parent already provides one). */
  hideRecordButton?: boolean;
}

// ─── Component ───

export function PaymentHistoryList({
  invoiceId,
  invoiceGrandTotal,
  onRecordPayment,
  hideRecordButton,
}: Props) {
  const { data: payments = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qInvoicePaymentsByInvoice(invoiceId),
  );
  const fmt = useFormatMoney();

  const { totalMoney, paidMoney, balanceMoney } = useMemo(() => {
    const total = phpFromMajor(invoiceGrandTotal ?? 0);
    const paid = sumMoney(payments.map((p) => phpFromCents(p.amount_cents ?? 0)));
    const balance = subtractMoney(total, paid);
    return { totalMoney: total, paidMoney: paid, balanceMoney: balance };
  }, [payments, invoiceGrandTotal]);

  const balanceCents = balanceMoney.getAmount();
  const isFullyPaid = balanceCents <= 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-4) var(--sp-5)',
        borderTop: '1px solid var(--border)',
      }}
    >
      {/* PRD.229 §6.2 / AC #8 — Section header + Add Payment action,
          mirroring the Adjustments section header so both money surfaces
          inside invoice detail follow the same layout. Bottom border
          matches the invoice header divider so each section reads as a
          distinct surface inside invoice detail. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
          paddingBottom: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-3)' }}>
          {/* PRD.229 §6.2 — Match the invoice header's prominence so the
              section stands out as its own surface inside invoice detail. */}
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Payments
          </h3>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Payments recorded against this invoice
          </span>
        </div>
        {!hideRecordButton && onRecordPayment && (
          <Button variant="gradient" size="md" onClick={onRecordPayment}>
            + Add Payment
          </Button>
        )}
      </div>

      {/* Summary strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-5)',
          flexWrap: 'wrap',
        }}
      >
        <SummaryStat label="Invoice total" value={fmt(totalMoney)} />
        <SummaryStat label="Paid so far" value={fmt(paidMoney)} color="var(--color-success-text)" />
        <SummaryStat
          label="Remaining"
          value={fmt(balanceMoney)}
          color={isFullyPaid ? 'var(--color-success-text)' : 'var(--color-warning-text)'}
        />
      </div>

      {/* History */}
      {payments.length === 0 ? (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            padding: 'var(--sp-3) 0',
          }}
        >
          No payments recorded yet.
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
                <Th>Date</Th>
                <Th align="right">Amount</Th>
                <Th>Method</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <Td>{formatDateTime(p.paid_at ?? p.created_at)}</Td>
                  <Td align="right" mono>
                    {fmt(phpFromCents(p.amount_cents ?? 0))}
                  </Td>
                  <Td>{methodLabel(p.method)}</Td>
                  <Td muted>{p.notes ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Cell helpers ───

function SummaryStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: color ?? 'var(--text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  muted,
}: {
  children: React.ReactNode;
  align?: 'right';
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        color: muted ? 'var(--text-muted)' : 'var(--text)',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}
    >
      {children}
    </td>
  );
}
